import React, { useState, useEffect, useMemo, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Papa from "papaparse";
import { ChevronLeft, ChevronRight, ZoomOut, ZoomIn } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/" + pdfjs.version + "/pdf.worker.min.js";

const getExt = (url = "") =>
  url.split("?")[0].split("#")[0].split(".").pop().toLowerCase();

const btnStyle = {
  border: "none",
  background: "transparent",
  padding: 4,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const TopToolbar = ({
  pageNumber,
  totalPages,
  zoom,
  fileName,
  prevPage,
  nextPage,
  zoomOut,
  zoomIn,
}) => (
  <div
    style={{
      height: 44,
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "0 16px",
      fontFamily: "Inter, sans-serif",
    }}
  >
    <span
      style={{
        background: "#f5f5f5",
        borderRadius: 6,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        minWidth: 32,
        textAlign: "center",
      }}
    >
      {pageNumber}
    </span>
    <span style={{ fontSize: 14, color: "#6b6b6b" }}>of {totalPages || "?"}</span>
    {/* <button onClick={prevPage} disabled={pageNumber === 1} style={btnStyle}>
      <ChevronLeft size={20} color="#c44dc0" strokeWidth={2.2} />
    </button>
    <button
      onClick={nextPage}
      disabled={totalPages && pageNumber === totalPages}
      style={btnStyle}
    >
      <ChevronRight size={20} color="#c44dc0" strokeWidth={2.2} />
    </button> */}
    <span
      style={{
        flex: 1,
        textAlign: "center",
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={fileName}
    >
      {/* {fileName} */}
    </span>
    <button onClick={zoomOut} style={btnStyle}><ZoomOut size={18} /></button>
    <span style={{ minWidth: 48, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
    <button onClick={zoomIn} style={btnStyle}><ZoomIn size={18} /></button>
  </div>
);

const SpreadsheetViewer = ({ fileUrl }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState(600);
  const [csvRows, setCsvRows] = useState([]);
  const [textContent, setTextContent] = useState("");
  const [loadError, setLoadError] = useState(null);

  const containerRef = useRef(null);
  const ext = useMemo(() => getExt(fileUrl), [fileUrl]);
  const fileName = useMemo(() => fileUrl.split("/").pop().split("?")[0], [fileUrl]);
  const encodedUrl = useMemo(() => encodeURIComponent(fileUrl || ""), [fileUrl]);
  const memoizedFile = useMemo(() => ({ url: fileUrl, withCredentials: false }), [fileUrl]);


const pageRefs = useRef([]);

useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      const visiblePages = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

      if (visiblePages.length > 0) {
        const visiblePage = visiblePages[0];
        const pageIndex = Number(visiblePage.target.getAttribute("data-page-index"));
        setPageNumber(pageIndex + 1);
      }
    },
    {
      root: containerRef.current,
      threshold: 0.5,
    }
  );

  pageRefs.current.forEach((ref) => ref && observer.observe(ref));

  return () => {
    pageRefs.current.forEach((ref) => ref && observer.unobserve(ref));
  };
}, [totalPages]);


  useEffect(() => {
    const updateWidth = () =>
      containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (ext === "csv") {
      fetch(fileUrl)
        .then((res) => res.text())
        .then((txt) => {
          const { data } = Papa.parse(txt.trim(), { skipEmptyLines: true });
          setCsvRows(data);
          setTotalPages(1);
        })
        .catch(() => setCsvRows([["❌ Failed to load CSV"]]));
    } else if (ext === "txt") {
      fetch(fileUrl)
        .then((res) => res.text())
        .then((txt) => {
          setTextContent(txt);
          setTotalPages(1);
        })
        .catch(() => setTextContent("❌ Failed to load TXT"));
    } else {
      setTotalPages(1);
    }
  }, [fileUrl, ext]);

  const csvViewer = (
    <div style={{ height: "88vh", overflow: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <tbody>
          {csvRows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c} style={{ border: "1px solid #ccc", padding: "4px 8px", fontFamily: "monospace" }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const officeIframe = (
    <iframe
      title="office-viewer"
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`}
      style={{ border: 0, width: "100%", height: "88vh" }}
    />
  );

  const textViewer = (
    <pre style={{ height: "88vh", overflow: "auto", padding: 16, background: "#f9f9f9", border: "1px solid #ccc", borderRadius: 8, fontFamily: "Consolas, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {textContent || "Loading..."}
    </pre>
  );

  const pdfViewer = (
  <Document
    file={memoizedFile}
    onLoadSuccess={({ numPages }) => {
      setTotalPages(numPages);
      setLoadError(null);
    }}
    onLoadError={(err) => {
      console.error(err);
      setLoadError("❌ Failed to load PDF");
    }}
    loading={<p>Loading PDF...</p>}
    error={<p>{loadError}</p>}
  >
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "24px" }}>
      {Array.from({ length: totalPages }, (_, i) => (
        <div
          key={`page_${i + 1}`}
          data-page-index={i}
          ref={(el) => (pageRefs.current[i] = el)}
        >
          <Page
            pageNumber={i + 1}
            width={containerWidth * zoom}
            renderAnnotationLayer={false}
          />
        </div>
      ))}
    </div>
  </Document>

  );

  let viewerContent;
if (["xls", "xlsx", "doc", "docx", "ppt", "pptx"].includes(ext)) {
  viewerContent = officeIframe;
} else if (ext === "csv") {
  viewerContent = csvViewer;
} else if (ext === "txt") {
  viewerContent = textViewer;
} else if (ext === "pdf") {
  viewerContent = pdfViewer;
} else if (["png", "jpg", "jpeg", "webp"].includes(ext)) {
  viewerContent = (
    <div style={{ height: "88vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img
        src={fileUrl}
        alt="Preview"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          borderRadius: 8,
          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        }}
      />
    </div>
  );
} else {
  viewerContent = (
    <div
      style={{
        height: "88vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #ccc",
        borderRadius: 8,
      }}
    >
      Unsupported file type: .{ext}
    </div>
  );
}
  return (
    <>
      <style>{`.textLayer span.highlighted { background: #ffff66; border-radius: 2px; color: #000 !important; -webkit-text-fill-color: #000 !important; }`}</style>
      <div ref={containerRef} style={{ position: "relative", width: "100%", height: "88vh", background: "#fff", border: "1px solid var(--Stroke-black, rgba(0, 0, 0, 0.10))", boxShadow: "0px 100px 28px 0px rgba(0,0,0,0), 0px 64px 26px 0px rgba(0,0,0,0.01), 0px 36px 22px 0px rgba(0,0,0,0.05), 0px 16px 16px 0px rgba(0,0,0,0.09), 0px 4px 9px 0px rgba(0,0,0,0.10)", borderRadius: 8, display: "flex", flexDirection: "column" }}>
      {ext === "pdf" && (  <TopToolbar pageNumber={pageNumber} totalPages={totalPages} zoom={zoom} fileName={fileName} prevPage={() => setPageNumber((p) => Math.max(p - 1, 1))} nextPage={() => setPageNumber((p) => totalPages ? Math.min(p + 1, totalPages) : p)} zoomOut={() => setZoom((z) => Math.max(z - 0.1, 0.5))} zoomIn={() => setZoom((z) => Math.min(z + 0.1, 2))} />)}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
            {viewerContent}
          </div>
        </div>
      </div>
    </>
  );
};

export default SpreadsheetViewer;
